import 'swiper/css'
import 'swiper/css/effect-fade'
import 'swiper/css/pagination'
import 'swiper/css/navigation'

import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import Image from 'next/image'
import { FC, MouseEvent, useRef, useState } from 'react'
import { type Swiper as SwiperType } from 'swiper'
import { Navigation, Pagination } from 'swiper/modules'
import { Swiper as SwiperComponent, SwiperSlide } from 'swiper/react'

import { FileApiType } from '@/api/shared/types'
import { ScreenSize } from '@/app/constants'
import { useCurrentBreakpoint } from '@/app/hooks/useCurrentBreakpoint'
import { cn } from '@/app/utils'
import { AnimatedLoader } from '@/components/shared/loader/ui/AnimatedLoader'

import styles from './PreviewImagesSlider.module.css'

type Props = {
  files: FileApiType[]
  onClose: () => void
  initialSlide: number
}

const PreviewImagesSlider: FC<Props> = ({ files, onClose, initialSlide }) => {
  const swiperRef = useRef<SwiperType | null>(null)
  const { breakpoint } = useCurrentBreakpoint()
  const isMobileScreen = [
    ScreenSize.Xxs,
    ScreenSize.Xs,
    ScreenSize.Sm
  ].includes(breakpoint)

  const [isLoaded, setIsLoaded] = useState(false)

  const handleSwiperClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement
    if (target.closest('.swiper-slide') || target.closest('.swiper-button')) {
      return
    }
    onClose()
  }

  const handleImageClick = (e: MouseEvent) => {
    e.stopPropagation()
  }

  const handleNext = (e: MouseEvent) => {
    e.stopPropagation()
    swiperRef.current?.slideNext()
  }

  const handlePrev = (e: MouseEvent) => {
    e.stopPropagation()
    swiperRef.current?.slidePrev()
  }

  return (
    <button
      type="button"
      className="fixed inset-0 z-50 flex w-full cursor-pointer items-center justify-center"
      onClick={handleSwiperClick}
    >
      {!isMobileScreen && (
        <>
          <button
            aria-label="Прокрутить влево"
            className={cn(
              'custom-prev absolute left-0 top-1/2 z-40 flex h-[90%] w-60 -translate-y-1/2 transform cursor-pointer items-center justify-center bg-transparent text-white transition-colors hover:bg-grey-40/30',
              styles.customPrev
            )}
            onClick={handlePrev}
          >
            <ChevronLeftIcon className="h-27 w-27 text-white" />
          </button>

          <button
            aria-label="Прокрутить вправо"
            className={cn(
              'absolute right-0 top-1/2 z-40 flex h-[90%] w-60 -translate-y-1/2 transform cursor-pointer items-center justify-center bg-transparent text-white transition-colors hover:bg-grey-40/30',
              styles.customNext
            )}
            onClick={handleNext}
          >
            <ChevronRightIcon className="h-27 w-27 text-white" />
          </button>
        </>
      )}

      <button
        type="button"
        className="relative max-h-full max-w-full"
        onClick={handleImageClick}
      >
        <SwiperComponent
          modules={[Navigation, Pagination]}
          slidesPerView="auto"
          initialSlide={initialSlide}
          effect="fade"
          loop={true}
          mousewheel
          watchOverflow
          threshold={10}
          grabCursor={true}
          navigation={{
            nextEl: '.custom-next',
            prevEl: '.custom-prev'
          }}
          onInit={(swiper: SwiperType) => (swiperRef.current = swiper)}
          wrapperClass="flex items-center justify-start"
        >
          {files.map(file => (
            <SwiperSlide key={file.link}>
              {!isLoaded && (
                <AnimatedLoader dataTestId="file-preview-slide-loader-id" />
              )}

              <Image
                src={file.link}
                alt={file.name}
                width={1920}
                height={1080}
                data-testid="file-preview-slide-image-id"
                className="!static mx-auto h-auto max-h-[90vh] w-auto object-contain px-[60px] py-0 xs:px-0"
                onLoad={() => setIsLoaded(true)}
              />
            </SwiperSlide>
          ))}
        </SwiperComponent>
      </button>
    </button>
  )
}

export { PreviewImagesSlider }
